import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import {
  RIGHT_ALIGN_BODY,
  RIGHT_ALIGN_HEADER,
} from '../../../../config/constants';

import { Paper } from '@mui/material';
import { useMemo } from 'react';
import { useFirestoreConnect } from 'react-redux-firebase';
import { useParams } from 'react-router-dom';
import ComponentGuard from '../../../../components/ComponentGuard';
import CreatedTableCellCopy from '../../../../components/CreatedTableCell';
import { useModal } from '../../../../components/Modal';
import Table from '../../../../components/Table';
import TableActionButton from '../../../../components/TableActionButton';
import { PORTAL_ROLES } from '../../../../config/roles';
import { EMPTY_ARRAY } from '../../../../shared/helpers';
import { onAssetListDelete } from '../../../../state/asset-list';
import { UrlParams } from '../../../../types/urlParams';

const columns = [
  { accessorKey: 'name', header: 'Name' },
  { accessorKey: 'fileName', header: 'File Name' },
  {
    accessorKey: 'created',
    header: 'Created',
    Cell: CreatedTableCellCopy,
  },
  {
    accessorKey: 'numRows',
    header: 'Assets',
    ...RIGHT_ALIGN_HEADER,
    ...RIGHT_ALIGN_BODY,
  },
];

const Edit = () => {
  const dispatch = useDispatch();
  const { showModal } = useModal();
  const { clientId } = useParams<UrlParams>();
  useFirestoreConnect([
    {
      collection: 'clients',
      doc: clientId,
      subcollections: [{ collection: 'assetLists' }],
      where: ['deleted', '==', 0],
      storeAs: 'assetLists',
      orderBy: ['created', 'desc'],
    },
  ]);
  const assetLists = useSelector(
    (state: RootStateOrAny) =>
      state.firestore.ordered.assetLists || EMPTY_ARRAY,
  );

  const deleteList = async (row) => {
    const { id } = row;
    const list = assetLists.find((l) => l.id === id);
    const messages = [`Are you sure you want to delete ${list.name}?`];

    const deleteConfirmed = await showModal({
      title: 'Please Confirm',
      buttons: {
        confirm: [{ text: 'Yes', value: true }],
        cancel: [{ text: 'No' }],
      },
      messages,
    });
    if (deleteConfirmed) {
      dispatch(onAssetListDelete({ id }));
    }
  };

  const onClickDownloadTaxonomy = async (rowData) => {
    const downloadAnchorNode = document.createElement('a');
    downloadAnchorNode.setAttribute('href', rowData.url);
    downloadAnchorNode.setAttribute('download', rowData.url);
    document.body.appendChild(downloadAnchorNode);
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
  };

  const rows = useMemo(
    () =>
      assetLists.map((assetList) => {
        const { id, name, fileName, numEmptyCells, numRows, created, url } =
          assetList;
        return {
          id,
          name,
          fileName,
          numEmptyCells,
          numRows,
          created,
          url,
        };
      }),
    [assetLists],
  );

  const actionButtons = ({ row }) => {
    const rowData = row.original;

    return [
      <ComponentGuard
        auth={{ atLeast: PORTAL_ROLES.LEAD_SURVEYOR }}
        key="Delete"
      >
        <TableActionButton
          text="Delete"
          colour="red"
          onClick={() => {
            deleteList(rowData);
          }}
        />
      </ComponentGuard>,

      <TableActionButton
        key="Download"
        text="Download"
        colour="green"
        onClick={() => onClickDownloadTaxonomy(rowData)}
      />,
    ];
  };

  return (
    <Paper sx={{ width: '100%' }} id="main-content">
      <Table
        rows={rows}
        columns={columns}
        tableProps={{
          renderRowActions: actionButtons,
          enableRowActions: true,
          positionActionsColumn: 'first',
        }}
      />
    </Paper>
  );
};

export default Edit;
